import { useEffect, useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { roleConfig } from '../constants/roleConfig';

export const useAddUserForm = (
  isOpen,
  loginRole,
  userDepartment,
  userSchemes,
) => {
  const [departments, setDepartments] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [fileTypesOptions, setFileTypesOptions] = useState([]);
  const [subDivisions, setSubDivisions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);
  const [loadingSubDivisions, setLoadingSubDivisions] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);

  const toast = useToast();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchUserDataAndDepartments = async () => {
      if (isOpen) {
        setLoadingDepartments(true);
        try {
          const res = await axiosInstance.get(
            `${baseUrl}/master/all-department`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const deptArray = Array.isArray(res.data?.data?.data)
            ? res.data.data.data
            : [];
          setDepartments(deptArray);

          if (loginRole === 'schemeAdmin' && userDepartment.length > 0) {
            const userDeptIds = userDepartment.map((dept) =>
              typeof dept === 'object' ? dept._id : dept,
            );
            const filteredDepts = deptArray.filter((dept) =>
              userDeptIds.includes(dept._id),
            );
            setAvailableDepartments(filteredDepts);
          } else {
            setAvailableDepartments(deptArray);
          }
        } catch (err) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Failed to load departments',
            status: 'error',
            duration: 3000,
          });
        } finally {
          setLoadingDepartments(false);
        }

        setLoadingSubDivisions(true);
        try {
          const res = await axiosInstance.get(
            `${baseUrl}/master/all-subdivisionsmain`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const subDivisionArray = Array.isArray(res.data?.data?.data?.data)
            ? res.data.data.data.data
            : [];
          setSubDivisions(subDivisionArray);
        } catch (err) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Failed to load sub-divisions',
            status: 'error',
            duration: 3000,
          });
        } finally {
          setLoadingSubDivisions(false);
        }
      }
    };

    fetchUserDataAndDepartments();
  }, [isOpen, token, toast, loginRole, userDepartment]);

  const fetchSchemes = useCallback(
    async (addUser, userSchemes, loginRole) => {
      if (
        !addUser.department ||
        addUser.department.length === 0 ||
        !addUser.role
      ) {
        setSchemes([]);
        return;
      }

      setLoadingSchemes(true);
      try {
        let allSchemes = [];

        if (addUser.department.length > 1) {
          const fetchPromises = addUser.department.map(async (deptId) => {
            try {
              const params = { department: deptId };
              const schemeRes = await axiosInstance.get(
                `${baseUrl}/master/all-scheme`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  params,
                },
              );

              if (schemeRes.status === 200) {
                return schemeRes?.data?.data?.data || [];
              }
              return [];
            } catch (err) {
              return [];
            }
          });

          const schemesArrays = await Promise.all(fetchPromises);
          allSchemes = schemesArrays.flat();

          const uniqueSchemeIds = new Set();
          allSchemes = allSchemes.filter((scheme) => {
            if (uniqueSchemeIds.has(scheme._id)) {
              return false;
            }
            uniqueSchemeIds.add(scheme._id);
            return true;
          });
        } else {
          const departmentId = addUser.department[0];
          const params = { department: departmentId };
          const schemeRes = await axiosInstance.get(
            `${baseUrl}/master/all-scheme`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params,
            },
          );

          if (schemeRes.status === 200) {
            allSchemes = schemeRes?.data?.data?.data || [];
          }
        }

        const roleCfg = roleConfig[addUser.role];
        if (roleCfg.allowedSchemes === 'pmJanman') {
          allSchemes = allSchemes.filter(
            (scheme) => scheme._id === '68ee2171ad9b09bdda13b4f6',
          );
        } else if (roleCfg.allowedSchemes === 'landAcquisition') {
          allSchemes = allSchemes.filter(
            (scheme) => scheme._id === '68d135b1e18cfc3e5248edc2',
          );
        } else if (roleCfg.allowedSchemes === 'general') {
          allSchemes = allSchemes.filter(
            (scheme) =>
              ![
                '68ee2171ad9b09bdda13b4f6',
                '68d135b1e18cfc3e5248edc2',
              ].includes(scheme._id),
          );
        }

        if (loginRole === 'schemeAdmin') {
          const userSchemeIds = userSchemes.map((s) =>
            typeof s === 'object' ? s._id : s,
          );
          allSchemes = allSchemes.filter((scheme) =>
            userSchemeIds.includes(scheme._id),
          );
        }

        setSchemes(allSchemes);
      } catch (err) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Error fetching schemes',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoadingSchemes(false);
      }
    },
    [token, toast],
  );

  const fetchFileTypes = useCallback(
    async (addUser) => {
      if (
        !['projectOfficer', 'subDivisionalOfficer'].includes(addUser.role) ||
        !addUser.schemes ||
        addUser.schemes.length === 0 ||
        !addUser.department ||
        addUser.department.length === 0
      ) {
        setFileTypesOptions([]);
        return;
      }

      setLoadingFileTypes(true);
      try {
        const schemesParam = Array.isArray(addUser.schemes)
          ? addUser.schemes.join(',')
          : addUser.schemes;

        const departmentParam = Array.isArray(addUser.department)
          ? addUser.department.join(',')
          : addUser.department;

        const response = await axiosInstance.get(
          `${baseUrl}/master/all-file-type`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              schemes: schemesParam,
              department: departmentParam,
            },
          },
        );

        if (response.data.status) {
          const fileTypesData = response.data?.data?.data || [];
          const uniqueFileTypes = [];
          const seenIds = new Set();

          fileTypesData.forEach((ft) => {
            if (!seenIds.has(ft._id)) {
              seenIds.add(ft._id);
              uniqueFileTypes.push(ft);
            }
          });

          const options = uniqueFileTypes.map((ft) => ({
            value: ft._id,
            label: `${ft.name}${ft.scheme?.name ? ` (${ft.scheme.name})` : ''}`,
          }));
          setFileTypesOptions(options);
        }
      } catch (err) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Failed to load file types',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoadingFileTypes(false);
      }
    },
    [token, toast],
  );

  const fetchTalukas = useCallback(
    async (role) => {
      if (role !== 'talukaUser') {
        setTalukas([]);
        return;
      }

      setLoadingTalukas(true);
      try {
        const res = await axiosInstance.get(`${baseUrl}/master/all-tahsils`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTalukas(res.data?.data?.data || []);
      } catch (err) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Failed to load talukas',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoadingTalukas(false);
      }
    },
    [token, toast],
  );

  return {
    departments,
    schemes,
    talukas,
    fileTypesOptions,
    subDivisions,
    loadingDepartments,
    loadingSchemes,
    loadingFileTypes,
    loadingSubDivisions,
    loadingTalukas,
    availableDepartments,
    fetchSchemes,
    fetchFileTypes,
    fetchTalukas,
  };
};
